import { useState } from 'react';
import { Lock, Sparkles, Loader2, Copy, Check, X, Send, Pencil, Wrench, ExternalLink, Eye, EyeOff } from 'lucide-react';
import { aiHelperApi, type AIHelperResponse } from '../api/ai_helper';
import { ticketsApi } from '../api/tickets';
import { Markdown } from '../lib/markdown';
import { fmtTime } from '../lib/format';
import { linkify, findAttachmentId } from '../lib/linkify';
import AttachmentPreviewModal from './AttachmentPreviewModal';

export interface TicketMessageData {
  id: string;
  author_name: string | null;
  author_email: string | null;
  body: string;
  is_internal: boolean;
  ai_generated?: boolean;
  created_at: string;
}

interface Props {
  m: TicketMessageData;
  ticketId: string;
  canUseAI: boolean;
  /** Кнопка «AI: ответить» в композере — поднимаем текст наверх. */
  onApplyToComposer?: (text: string) => void;
  /** Если тикет эскалирован в Redmine — подставляется здесь, чтобы рисовать
   *  ссылку «Открыть в Redmine» на сообщениях, пришедших оттуда. */
  redmineUrl?: string | null;
  redmineIssueId?: number | null;
  /** Перерисовать карточку тикета после изменения видимости сообщения. */
  onChanged?: () => void;
  /** Может ли текущий пользователь переключать видимость internal-сообщений. */
  canToggleVisibility?: boolean;
}

/** Сообщение пришло из Redmine reverse-sync (services/redmine_sync.py). */
function isFromRedmine(m: TicketMessageData): boolean {
  const e = (m.author_email || '').toLowerCase();
  return e.startsWith('redmine-') || e.endsWith('@redmine.iqr.su');
}

export default function TicketMessage({ m, ticketId, canUseAI, onApplyToComposer, redmineUrl, redmineIssueId, onChanged, canToggleVisibility }: Props) {
  const [aiOpen, setAiOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<AIHelperResponse | null>(null);
  const [showDraft, setShowDraft] = useState(false);
  const [copied, setCopied] = useState(false);
  const [edited, setEdited] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const attId = findAttachmentId(m.body);

  const runAI = async () => {
    setLoading(true); setErr(null); setResult(null); setShowDraft(false); setAiOpen(true);
    try {
      const r = await aiHelperApi.run(ticketId, 'reply', undefined, { message_id: m.id, refine: true });
      setResult(r);
      setEdited(r.text);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setLoading(false); }
  };

  const copy = () => {
    const text = edited ?? result?.text ?? '';
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };

  const fromRedmine = isFromRedmine(m);
  return (
    <div className={`border rounded-xl p-4 ${fromRedmine
      ? 'bg-amber-900/10 border-amber-800/50'
      : m.is_internal
        ? 'bg-yellow-900/10 border-yellow-900/40'
        : 'bg-slate-900 border-slate-800'}`}>
      {fromRedmine && (
        <div className="flex items-center gap-1.5 mb-2 -mt-1 -mx-1 px-2 py-1 bg-amber-900/30 rounded text-[10px] uppercase tracking-wider text-amber-300 font-bold">
          <Wrench size={11} /> Из Redmine (L3)
          {redmineUrl && (
            <a href={redmineUrl} target="_blank" rel="noreferrer"
               className="ml-auto inline-flex items-center gap-1 text-amber-200 hover:text-white normal-case tracking-normal font-normal">
              открыть issue{redmineIssueId ? ` #${redmineIssueId}` : ''}<ExternalLink size={10} />
            </a>
          )}
        </div>
      )}
      <div className="flex items-center justify-between mb-2 text-xs">
        <span className="flex items-center gap-1.5">
          {fromRedmine
            ? <Wrench size={10} className="text-amber-400" />
            : m.is_internal && <Lock size={10} className="text-yellow-500" />}
          <span className="font-bold text-white">{m.author_name || m.author_email || 'Гость'}</span>
          {m.ai_generated && <span className="text-purple-400 text-[10px] uppercase">AI</span>}
        </span>
        <span className="flex items-center gap-2">
          {canToggleVisibility && (
            <button
              onClick={async () => {
                const verb = m.is_internal ? 'показать клиенту' : 'скрыть от клиента';
                if (!window.confirm(`Точно ${verb}?`)) return;
                try {
                  await ticketsApi.toggleMessageVisibility(ticketId, m.id);
                  onChanged?.();
                } catch (e: any) {
                  alert('Не удалось: ' + (e?.response?.data?.detail || e?.message));
                }
              }}
              title={m.is_internal ? 'Сделать сообщение видимым клиенту' : 'Скрыть сообщение от клиента (internal)'}
              className={`text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded border transition ${
                m.is_internal
                  ? 'text-blue-300 hover:text-white bg-blue-900/20 hover:bg-blue-700 border-blue-900/50'
                  : 'text-yellow-300 hover:text-white bg-yellow-900/20 hover:bg-yellow-700 border-yellow-900/50'
              }`}>
              {m.is_internal ? <><Eye size={11} /> показать клиенту</> : <><EyeOff size={11} /> скрыть</>}
            </button>
          )}
          {canUseAI && !aiOpen && !fromRedmine && (
            <button onClick={runAI}
              title="AI: сформировать ответ именно на это сообщение"
              className="text-[11px] inline-flex items-center gap-1 text-purple-300 hover:text-purple-200 bg-purple-900/20 hover:bg-purple-900/40 border border-purple-900/50 px-2 py-0.5 rounded">
              <Sparkles size={11} /> AI: ответить
            </button>
          )}
          <span className="text-slate-500">{fmtTime(m.created_at)}</span>
        </span>
      </div>
      <p className="text-slate-200 whitespace-pre-wrap text-sm leading-relaxed">
        {linkify(m.body, (id) => setPreviewId(id))}
      </p>
      {attId && (
        <button onClick={() => setPreviewId(attId)}
          className="mt-2 text-xs inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 px-2.5 py-1 rounded">
          Открыть вложение
        </button>
      )}
      {previewId && (
        <AttachmentPreviewModal attachmentId={previewId}
          onClose={() => setPreviewId(null)} />
      )}

      {aiOpen && (
        <div className="mt-3 border-t border-slate-800 pt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-wider text-purple-300 flex items-center gap-1.5">
              <Sparkles size={11} /> AI-черновик ответа на это сообщение
              {result && result.draft && result.draft !== result.text && (
                <span className="text-[9px] text-slate-500">(2 прогона: черновик → улучшение)</span>
              )}
            </span>
            <button onClick={() => { setAiOpen(false); setResult(null); setErr(null); }}
              className="text-slate-400 hover:text-white" title="Закрыть">
              <X size={13} />
            </button>
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-xs text-slate-400 py-3">
              <Loader2 size={14} className="animate-spin" />
              <span>AI собирает ответ (2 прохода: черновик → улучшение)…</span>
            </div>
          )}
          {err && (
            <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div>
          )}

          {result && !loading && (
            <>
              <div className="bg-slate-950/60 border border-slate-800 rounded p-3">
                <textarea
                  value={edited ?? result.text}
                  onChange={e => setEdited(e.target.value)}
                  rows={Math.min(20, Math.max(6, (edited ?? result.text).split('\n').length + 1))}
                  className="w-full bg-transparent text-sm text-slate-100 font-sans leading-relaxed focus:outline-none resize-y"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-2">
                <button onClick={() => onApplyToComposer?.(edited ?? result.text)}
                  className="text-xs inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white font-bold px-3 py-1.5 rounded">
                  <Send size={12} /> Вставить в форму ответа
                </button>
                <button onClick={copy} className="text-xs inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1.5 rounded">
                  {copied ? <><Check size={11} /> Скопировано</> : <><Copy size={11} /> Скопировать</>}
                </button>
                <button onClick={runAI} className="text-xs inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded">
                  <Sparkles size={11} /> Перегенерировать
                </button>
                {result.draft && result.draft !== result.text && (
                  <button onClick={() => setShowDraft(d => !d)}
                    className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1 ml-auto">
                    <Pencil size={10} /> {showDraft ? 'скрыть' : 'показать'} черновик 1-го прогона
                  </button>
                )}
              </div>

              {showDraft && result.draft && (
                <div className="mt-2 bg-slate-950/40 border border-dashed border-slate-700 rounded p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                    Черновик до улучшения
                  </div>
                  <div className="text-xs text-slate-400 leading-relaxed"><Markdown source={result.draft} /></div>
                </div>
              )}

              {result.sources && result.sources.length > 0 && (
                <div className="mt-2 text-[10px] text-slate-500">
                  <span className="mr-1">RAG-источники:</span>
                  {result.sources.map((s, i) => (
                    <span key={i} className="inline-block bg-slate-800 rounded px-1.5 py-0.5 mr-1 mb-1">
                      [{(s.score*100|0)}%] {s.filename}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}


